package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.MobileDashboardBackHandler
import org.example.project.enginekalender.KalenderWeekDetail
import org.example.project.enginekalender.KalenderAufgabenCalendar
import org.example.project.enginekalender.Monatsübersicht
import org.example.project.enginekalender.kalenderBookingMarkerColorHex
import org.example.project.enginekalender.kalenderBookingIsVacation
import org.example.project.enginekalender.kalenderDayState
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.loadKalenderBookings
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.enginepersonal.ZeitApi
import org.example.project.enginepersonal.ZeitMobileDayPopup
import org.example.project.enginepersonal.ZeitMonth
import org.example.project.enginepersonal.ZeitMobileDayInfo
import org.example.project.enginepersonal.ZeitMobileMonthSummary
import org.example.project.enginepersonal.ZeitprofilConfig
import org.example.project.enginepersonal.UrlaubsuebersichtDayPopup
import org.example.project.enginepersonal.defaultZeitprofil
import org.example.project.enginepersonal.zeitMobileDayInfo
import org.example.project.enginepersonal.zeitMobileProfileHourRange
import org.example.project.enginepersonal.zeitMobileMonthLabel
import org.example.project.enginepersonal.zeitMobileMonthSummary
import org.example.project.manager.NodeScope
import org.example.project.screens_cus.Abwesenheitsbedarf
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.ZeitkontoJournalEintrag
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import org.w3c.dom.TouchEvent
import kotlin.js.Date
import kotlin.math.abs
import kotlinx.browser.window

@Composable
fun MitarbeiterKalender() {
    val userId = LoginService.getId()
    val refreshVersion = maxOf(
        GlobalManager.jsonDomainVersion("kalender"),
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("zeit")
    )
    var selectedDate by remember { mutableStateOf(kalenderTodayIsoDate()) }
    var detailMode by remember { mutableStateOf("Monat") }
    var aufgabenModus by remember { mutableStateOf(false) }
    var aufgabenCopyKind by remember { mutableStateOf("") }
    var aufgabenCopySource by remember { mutableStateOf("") }
    var aufgabenCopyRequestToken by remember { mutableStateOf(0) }
    var isPhone by remember { mutableStateOf(window.innerWidth <= 760) }
    var monthDayOpen by remember { mutableStateOf(false) }
    var selectedBookingCell by remember { mutableStateOf<Pair<String, Int>?>(null) }
    var termine by remember { mutableStateOf<List<All>>(emptyList()) }
    var timeDays by remember { mutableStateOf<List<All>>(emptyList()) }
    var profileConfig by remember { mutableStateOf(ZeitprofilConfig()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf("") }
    var loadedOnce by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val onResize: (Event) -> Unit = { isPhone = window.innerWidth <= 760 }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }
    LaunchedEffect(isPhone) {
        if (isPhone) {
            aufgabenCopyKind = ""
            detailMode = "Monat"
            monthDayOpen = false
        }
    }

    val selectedMonth = selectedDate.take(7)
    val mobileOnlyAccess = LoginService.getUser()?.let { it.zugangMobil && !it.zugangDesktop } == true
    val profileSlotOverride = if (mobileOnlyAccess) {
        mitarbeiterKalenderProfileSlot(userId, selectedDate, timeDays, profileConfig)
    } else {
        null
    }

    LaunchedEffect(userId, refreshVersion, selectedMonth) {
        if (!loadedOnce) loading = true
        error = ""
        runCatching {
            val month = mitarbeiterZeitMonthFromIso(selectedDate)
            val loadedProfiles = ZeitApi.loadProfiles().let { loaded ->
                if (loaded.profile.isEmpty()) ZeitprofilConfig(profile = listOf(defaultZeitprofil())) else loaded
            }
            Triple(loadKalenderBookings(NodeScope.CUS, userId), ZeitApi.loadMyMonth(month), loadedProfiles)
        }
            .onSuccess {
                termine = it.first
                timeDays = it.second
                profileConfig = it.third
                loadedOnce = true
            }
            .onFailure {
                termine = emptyList()
                timeDays = emptyList()
                profileConfig = ZeitprofilConfig(profile = listOf(defaultZeitprofil()))
                error = it.message ?: "Termine konnten nicht geladen werden."
            }
        loading = false
    }

    val sichtbareTermine = kalenderDayState(
        termine.filter { mitarbeiterKalenderDatum(it.datum) == selectedDate }
    ).bookings
        .sortedWith(compareBy<All> { it.uhrzeitVon }.thenBy { it.bezeichnung })

    fun shiftPeriod(delta: Int) {
        selectedDate = when (detailMode) {
            "Monat" -> "${mitarbeiterShiftMonth(selectedDate.take(7), delta)}-01"
            "Tag" -> mitarbeiterShiftIsoDate(selectedDate, delta)
            else -> mitarbeiterShiftIsoDate(selectedDate, delta * 7)
        }
        if (detailMode == "Monat") monthDayOpen = false
    }

    DisposableEffect(monthDayOpen) {
        val backHandler = {
            if (monthDayOpen) {
                monthDayOpen = false
                true
            } else {
                false
            }
        }
        MobileDashboardBackHandler.handler.value = backHandler
        onDispose {
            if (MobileDashboardBackHandler.handler.value === backHandler) {
                MobileDashboardBackHandler.handler.value = null
            }
        }
    }

    Style(MitarbeiterMobileStyles); Style(MitarbeiterTabletStyles)
    Div({ classes(MitarbeiterMobileStyles.page, MitarbeiterTabletStyles.calendarPage) }) {
        Div({ classes(MitarbeiterMobileStyles.header, MitarbeiterTabletStyles.mobileCalendarHeaderHidden) }) {
            H3({ classes(MitarbeiterMobileStyles.title) }) { Text("Kalender") }
        }

        Div({ classes(MitarbeiterTabletStyles.periodBar) }) {
            Div({ classes(MitarbeiterTabletStyles.periodModes); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                if (isPhone) {
                    property("width", "100%")
                    justifyContent(JustifyContent.Center)
                    gap(12.px)
                } else {
                    justifyContent(JustifyContent.Center)
                    gap(6.px)
                }
                property("flex-wrap", "nowrap"); flexShrink(0) } }) {
                LumaticaButton(label = "Termine", onClick = { aufgabenModus = false; aufgabenCopyKind = ""; monthDayOpen = false },
                    variant = LumaticaButtonVariant.State, isActive = !aufgabenModus, heightPx = 32)
                LumaticaButton(label = "Aufgaben", onClick = { aufgabenModus = true; monthDayOpen = false },
                    variant = LumaticaButtonVariant.State, isActive = aufgabenModus, heightPx = 32)
            }
            Div({ classes(MitarbeiterTabletStyles.periodNav) }) {
                MitarbeiterKalenderPeriodButton("<") { shiftPeriod(-1) }
                Span({ classes(MitarbeiterTabletStyles.periodLabel) }) {
                    Text(mitarbeiterKalenderPeriodLabel(detailMode, selectedDate))
                }
                MitarbeiterKalenderPeriodButton(">") { shiftPeriod(1) }
            }
            Div({ classes(MitarbeiterTabletStyles.periodViews); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.FlexEnd)
                gap(6.px); property("flex-wrap", "nowrap"); minWidth(0.px) } }) {
                if (!isPhone) listOf("Monat", "Tag", "Woche").forEach { option ->
                    LumaticaButton(label = option, onClick = { detailMode = option; monthDayOpen = false },
                        variant = LumaticaButtonVariant.State, isActive = detailMode == option,
                        heightPx = 32, paddingH = 10)
                }
                if (aufgabenModus && !isPhone) {
                    LumaticaButton(label = "Tag markieren", onClick = {
                        if (aufgabenCopyKind == "Tag") aufgabenCopyKind = "" else {
                            aufgabenCopyKind = "Tag"
                            aufgabenCopySource = selectedDate
                        }
                    }, variant = LumaticaButtonVariant.State, isActive = aufgabenCopyKind == "Tag", heightPx = 32)
                    LumaticaButton(label = "Woche markieren", onClick = {
                        if (aufgabenCopyKind == "Woche") aufgabenCopyKind = "" else {
                            aufgabenCopyKind = "Woche"
                            aufgabenCopySource = selectedDate
                        }
                    }, variant = LumaticaButtonVariant.State, isActive = aufgabenCopyKind == "Woche", heightPx = 32)
                    if (aufgabenCopyKind.isNotBlank()) {
                        LumaticaButton(label = "Übernehmen auf", onClick = { aufgabenCopyRequestToken += 1 }, heightPx = 32)
                    }
                }
            }
        }

        if (aufgabenModus) {
            Div({ classes(MitarbeiterTabletStyles.content) }) {
                KalenderAufgabenCalendar(
                    selectedDate = selectedDate,
                    onSelectDate = { selectedDate = it },
                    art = NodeScope.CUS,
                    kalenderUserId = userId,
                    ansicht = detailMode,
                    monthDayOpen = monthDayOpen,
                    onMonthDayOpen = { monthDayOpen = it },
                    copyKind = aufgabenCopyKind,
                    copySource = aufgabenCopySource,
                    copyRequestToken = aufgabenCopyRequestToken,
                    onCopyComplete = { aufgabenCopyKind = "" },
                    editMode = true,
                    compact = true
                )
            }
        } else when {
            loading -> MitarbeiterKalenderHinweis("Termine werden geladen.")
            error.isNotBlank() -> MitarbeiterKalenderHinweis(error, error = true)
            else -> MitarbeiterKalenderTabletArbeitsflaeche(
                userId = userId,
                selectedDate = selectedDate,
                mode = detailMode,
                monthDayOpen = monthDayOpen,
                termine = termine,
                sichtbareTermine = sichtbareTermine,
                timeDays = timeDays,
                profileConfig = profileConfig,
                slotTimeOverride = profileSlotOverride,
                selectedBookingCell = selectedBookingCell,
                onSelectedBookingCellChange = { selectedBookingCell = it },
                onSelectDate = { nextDate ->
                    selectedDate = nextDate
                    if (detailMode == "Monat") monthDayOpen = true
                },
                onShiftPeriod = ::shiftPeriod,
                onShiftDay = { delta ->
                    selectedDate = mitarbeiterShiftIsoDate(selectedDate, delta)
                },
                onMonthDayOpenChange = { open -> monthDayOpen = open }
            )
        }
    }
}

@Composable
private fun MitarbeiterKalenderTabletArbeitsflaeche(
    userId: String,
    selectedDate: String,
    mode: String,
    monthDayOpen: Boolean,
    termine: List<All>,
    sichtbareTermine: List<All>,
    timeDays: List<All>,
    profileConfig: ZeitprofilConfig,
    slotTimeOverride: Pair<String, String>?,
    selectedBookingCell: Pair<String, Int>?,
    onSelectedBookingCellChange: (Pair<String, Int>) -> Unit,
    onSelectDate: (String) -> Unit,
    onShiftPeriod: (Int) -> Unit,
    onShiftDay: (Int) -> Unit,
    onMonthDayOpenChange: (Boolean) -> Unit
) {
    var swipeStartX by remember { mutableStateOf<Double?>(null) }
    var swipeStartY by remember { mutableStateOf<Double?>(null) }

    fun finishSwipe(clientX: Double) {
        val startX = swipeStartX ?: return
        swipeStartX = null
        swipeStartY = null
        val deltaX = clientX - startX
        if (abs(deltaX) <= 80.0) return
        if (mode == "Monat" && monthDayOpen) {
            if (deltaX < 0) onShiftDay(1) else onShiftDay(-1)
        } else {
            if (deltaX < 0) onShiftPeriod(1) else onShiftPeriod(-1)
        }
    }

    fun finishTouchSwipe(clientX: Double, clientY: Double) {
        val startX = swipeStartX ?: return
        val startY = swipeStartY ?: return
        swipeStartX = null
        swipeStartY = null
        val deltaX = clientX - startX
        val deltaY = clientY - startY

        if (mode == "Monat" && monthDayOpen && deltaY > 80.0 && abs(deltaY) > abs(deltaX) * 1.25) {
            onMonthDayOpenChange(false)
            return
        }
        if (abs(deltaX) <= 80.0 || abs(deltaX) < abs(deltaY) * 1.25) return
        if (mode == "Monat" && monthDayOpen) {
            if (deltaX < 0) onShiftDay(1) else onShiftDay(-1)
        } else {
            if (deltaX < 0) onShiftPeriod(1) else onShiftPeriod(-1)
        }
    }

    Div({
        classes(MitarbeiterTabletStyles.shell)
        if (mode == "Monat" && monthDayOpen) classes(MitarbeiterTabletStyles.shellWithDay)
    }) {
        Div({
            classes(MitarbeiterTabletStyles.content)
            if (mode == "Monat" && monthDayOpen) classes(MitarbeiterTabletStyles.contentWithDay)
            if (mode == "Woche" || mode == "Tag" || mode == "Monat") {
                onMouseDown { swipeStartX = it.clientX.toDouble() }
                onMouseUp { finishSwipe(it.clientX.toDouble()) }
                onMouseLeave {
                    swipeStartX = null
                    swipeStartY = null
                }
                onTouchStart {
                    val touch = it.touches.item(0) ?: return@onTouchStart
                    swipeStartX = touch.clientX.toDouble()
                    swipeStartY = touch.clientY.toDouble()
                }
                onTouchEnd {
                    val touchEvent = it.nativeEvent as? TouchEvent ?: return@onTouchEnd
                    val touch = touchEvent.changedTouches.item(0) ?: return@onTouchEnd
                    finishTouchSwipe(touch.clientX.toDouble(), touch.clientY.toDouble())
                }
            }
        }) {
            when (mode) {
                "Monat" -> {
                    Div({
                        classes(MitarbeiterTabletStyles.monthPane)
                        if (monthDayOpen) classes(MitarbeiterTabletStyles.monthPaneShifted)
                        if (monthDayOpen) classes(MitarbeiterTabletStyles.mobileMonthCollapsed)
                        onMouseDown { swipeStartX = it.clientX.toDouble() }
                        onMouseUp { finishSwipe(it.clientX.toDouble()) }
                        onMouseLeave {
                            swipeStartX = null
                            swipeStartY = null
                        }
                        onTouchStart {
                            val touch = it.touches.item(0) ?: return@onTouchStart
                            swipeStartX = touch.clientX.toDouble()
                            swipeStartY = touch.clientY.toDouble()
                        }
                        onTouchEnd {
                            val touchEvent = it.nativeEvent as? TouchEvent ?: return@onTouchEnd
                            val touch = touchEvent.changedTouches.item(0) ?: return@onTouchEnd
                            finishTouchSwipe(touch.clientX.toDouble(), touch.clientY.toDouble())
                        }
                    }) {
                        Monatsübersicht(
                            selectedDate = selectedDate,
                            art = NodeScope.CUS,
                            kalenderUserId = userId,
                            bookingsOverride = termine,
                            showBookingHover = false,
                            onSelectDate = onSelectDate
                        )
                    }
                    if (monthDayOpen) {
                        Div({ classes(MitarbeiterTabletStyles.mobileDayBelow) }) {
                            Div({ classes(MitarbeiterTabletStyles.dayPaneInner) }) {
                                KalenderWeekDetail(
                                    selectedDate = selectedDate,
                                    art = NodeScope.CUS,
                                    kalenderUserId = userId,
                                    ansicht = "Tag",
                                    fillVisibleHours = true,
                                    selectedBookingCell = selectedBookingCell,
                                    onSelectedBookingCellChange = onSelectedBookingCellChange,
                                    bookingRequestToken = 0,
                                    settingsRequestToken = 0,
                                    canManageSlotSettings = false,
                                    slotTimeOverride = slotTimeOverride,
                                    bookingFilter = { true }
                                )
                            }
                        }
                    }
                    if (monthDayOpen) {
                        Div({ classes(MitarbeiterTabletStyles.dayPane) }) {
                            Div({ classes(MitarbeiterTabletStyles.dayPaneInner) }) {
                                KalenderWeekDetail(
                                    selectedDate = selectedDate,
                                    art = NodeScope.CUS,
                                    kalenderUserId = userId,
                                    ansicht = "Tag",
                                    fillVisibleHours = true,
                                    selectedBookingCell = selectedBookingCell,
                                    onSelectedBookingCellChange = onSelectedBookingCellChange,
                                    bookingRequestToken = 0,
                                    settingsRequestToken = 0,
                                    canManageSlotSettings = false,
                                    slotTimeOverride = slotTimeOverride,
                                    bookingFilter = { true }
                                )
                            }
                        }
                    }
                }

                "Woche" -> {
                    Div({ classes(MitarbeiterTabletStyles.weekPane) }) {
                        KalenderWeekDetail(
                            selectedDate = selectedDate,
                            art = NodeScope.CUS,
                            kalenderUserId = userId,
                            ansicht = "Woche",
                            selectedBookingCell = selectedBookingCell,
                            onSelectedBookingCellChange = onSelectedBookingCellChange,
                            bookingRequestToken = 0,
                            settingsRequestToken = 0,
                            canManageSlotSettings = false,
                            slotTimeOverride = slotTimeOverride,
                            bookingFilter = { true }
                        )
                    }
                }

                else -> {
                    Div({ classes(MitarbeiterTabletStyles.dayPaneFull) }) {
                        KalenderWeekDetail(
                            selectedDate = selectedDate,
                            art = NodeScope.CUS,
                            kalenderUserId = userId,
                            ansicht = "Tag",
                            fillVisibleHours = true,
                            selectedBookingCell = selectedBookingCell,
                            onSelectedBookingCellChange = onSelectedBookingCellChange,
                            bookingRequestToken = 0,
                            settingsRequestToken = 0,
                            canManageSlotSettings = false,
                            slotTimeOverride = slotTimeOverride,
                            bookingFilter = { true }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun MitarbeiterKalenderPeriodButton(label: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(MitarbeiterTabletStyles.periodButton)
        onClick { onClick() }
    }) {
        Text(label)
    }
}

private fun mitarbeiterIstTerminKalendereintrag(entry: All): Boolean {
    if (kalenderBookingIsVacation(entry)) return false

    val blockedTerms = setOf(
        "urlaub",
        "abwesenheit",
        "freizeitausgleich",
        "frei",
        "unbezahlt frei",
        "sonderurlaub"
    )
    val labels = buildList {
        add(entry.art)
        add(entry.abwesenheitsart)
        add(entry.bezeichnung)
        add(entry.titel.orEmpty())
        add(entry.typ)
        add(entry.quelle)
    }.map { it.trim().lowercase() }

    if (labels.any { label -> label in blockedTerms || blockedTerms.any { term -> label.contains(term) } }) {
        return false
    }

    val art = entry.art.trim().lowercase()
    return art == "termin" ||
        entry.bezeichnung.isNotBlank() ||
        entry.uhrzeitVon.isNotBlank() ||
        entry.uhrzeitBis.isNotBlank()
}

private fun mitarbeiterKalenderPeriodLabel(mode: String, selectedDate: String): String {
    if (mode == "Monat") return mitarbeiterMonthLabel(selectedDate.take(7))

    val days = mitarbeiterWeekDays(selectedDate)
    val start = days.firstOrNull()?.let(::mitarbeiterKalenderDatumsLabel).orEmpty()
    val end = days.lastOrNull()?.let(::mitarbeiterKalenderDatumsLabel).orEmpty()
    return if (start.isNotBlank() && end.isNotBlank()) "$start - $end" else "Woche"
}

private fun mitarbeiterKalenderProfileSlot(
    userId: String,
    selectedDate: String,
    timeDays: List<All>,
    profileConfig: ZeitprofilConfig
): Pair<String, String>? {
    val record = timeDays.firstOrNull { it.userId == userId && it.datum == selectedDate }
    val profileId = record?.zeitprofilId?.takeIf { it.isNotBlank() }
        ?: profileConfig.zuordnungen[userId].orEmpty()
    val profile = profileConfig.profile.firstOrNull { it.id == profileId } ?: return null
    val days = profile.arbeitstage.takeIf { it.isNotEmpty() } ?: mapOf(
        "mo" to true,
        "di" to true,
        "mi" to true,
        "do" to true,
        "fr" to true,
        "sa" to false,
        "so" to false
    )
    if (days[mitarbeiterKalenderDayKey(selectedDate)] != true) return null
    val start = profile.start.takeIf(::mitarbeiterKalenderValidClock) ?: return null
    val end = profile.ende.takeIf(::mitarbeiterKalenderValidClock) ?: return null
    if (mitarbeiterZeitMinuten(end) <= mitarbeiterZeitMinuten(start)) return null
    return start to end
}

private fun mitarbeiterKalenderDayKey(iso: String): String {
    val parsed = parseKalenderIsoDate(iso) ?: return "mo"
    val date = Date(
        parsed.year.takeIf { it > 0 } ?: return "mo",
        (parsed.month.takeIf { it > 0 } ?: return "mo") - 1,
        parsed.day.takeIf { it > 0 } ?: return "mo"
    )
    return when (date.getDay().toInt()) {
        0 -> "so"
        1 -> "mo"
        2 -> "di"
        3 -> "mi"
        4 -> "do"
        5 -> "fr"
        6 -> "sa"
        else -> "mo"
    }
}

private fun mitarbeiterKalenderValidClock(value: String): Boolean =
    Regex("""\d{2}:\d{2}""").matches(value) && mitarbeiterZeitMinuten(value) >= 0

@Composable
private fun MitarbeiterKalenderWeekStrip(
    selectedDate: String,
    termine: List<All>,
    onSelectDate: (String) -> Unit
) {
    Div({ classes(MitarbeiterMobileStyles.weekStrip) }) {
        mitarbeiterWeekDays(selectedDate).forEach { day ->
            val dayBookings = kalenderDayState(
                termine.filter { mitarbeiterKalenderDatum(it.datum) == day }
            )
            Button(attrs = {
                classes(MitarbeiterMobileStyles.weekDayButton)
                if (day == selectedDate) classes(MitarbeiterMobileStyles.weekDayButtonActive)
                onClick { onSelectDate(day) }
            }) {
                Span({ classes(MitarbeiterMobileStyles.weekDayName) }) {
                    Text(mitarbeiterKalenderWochentag(day))
                }
                Span({ classes(MitarbeiterMobileStyles.weekDayNumber) }) {
                    Text(day.takeLast(2).trimStart('0').ifBlank { "0" })
                }
                if (dayBookings.bookings.isNotEmpty()) {
                    Span({ classes(MitarbeiterMobileStyles.weekDayCount) }) {
                        Text(dayBookings.bookings.size.toString())
                    }
                }
            }
        }
    }
}


@Composable
private fun MitarbeiterStundenTag(
    userId: String,
    selectedDate: String,
    termine: List<All>,
    timeDays: List<All>,
    profileConfig: ZeitprofilConfig
) {
    val tagesLabels = termine.filter { mitarbeiterIstGanztagsUrlaub(it) }
    val stundenTermine = termine.filterNot { mitarbeiterIstGanztagsUrlaub(it) }
    val profileHours = zeitMobileProfileHourRange(userId, selectedDate, timeDays, profileConfig)
    val visibleHours = profileHours ?: mitarbeiterArbeitsstundenRange(stundenTermine)
    Div({ classes(MitarbeiterMobileStyles.daySchedule) }) {
        Div({ classes(MitarbeiterMobileStyles.dayScheduleHeader) }) {
            Span { Text(mitarbeiterKalenderDatumsLabel(selectedDate)) }
            Span { Text("${termine.size} Termine") }
        }
        if (tagesLabels.isNotEmpty()) {
            Div({ classes(MitarbeiterMobileStyles.dayLabels) }) {
                tagesLabels.distinctBy { it.id.ifBlank { "${it.datum}_${it.bezeichnung}" } }.forEach { termin ->
                    val markerColor = kalenderBookingMarkerColorHex(listOf(termin)) ?: "#5b9bff"
                    Span({
                        classes(MitarbeiterMobileStyles.dayLabel)
                        style {
                            property("background", "color-mix(in srgb, $markerColor 24%, transparent)")
                            property("border", "1px solid color-mix(in srgb, $markerColor 48%, transparent)")
                        }
                    }) {
                        Text(mitarbeiterUrlaubLabel(termin))
                    }
                }
            }
        }
        if (visibleHours == null) {
            Div({ classes(MitarbeiterMobileStyles.emptyDetail) }) {
                Text(if (tagesLabels.isEmpty()) "Kein Arbeitstag laut Zeitprofil." else "Ganztagseintrag")
            }
        } else {
            visibleHours.forEach { hour ->
                val hourTermine = stundenTermine.filter { termin -> mitarbeiterTerminBelegtStunde(termin, hour) }
                val startingTermine = hourTermine.filter { termin ->
                    mitarbeiterTerminStartetInStunde(termin, hour, visibleHours.first)
                }
                val markerColor = kalenderBookingMarkerColorHex(hourTermine)
                Div({
                    classes(MitarbeiterMobileStyles.hourRow, MitarbeiterMobileStyles.normalHourRow)
                    if (markerColor != null) {
                        style {
                            property("background", "color-mix(in srgb, $markerColor 12%, transparent)")
                        }
                    }
                }) {
                    Span({ classes(MitarbeiterMobileStyles.hourLabel) }) {
                        Text(hour.toString().padStart(2, '0') + ":00")
                    }
                    Div({ classes(MitarbeiterMobileStyles.hourContent) }) {
                        if (hourTermine.isEmpty()) {
                            Span({ classes(MitarbeiterMobileStyles.emptyHour) }) { Text("-") }
                        } else {
                            startingTermine.forEach { termin -> MitarbeiterTerminSlot(termin) }
                        }
                    }
                }
            }
        }
    }
}
